import React from 'react';

import { stopPropagation } from '@neptune/shared/common-util';
import { bemBlock, Button, Icon, Layout, Text } from '@neptune/shared/venus-ui';

import { InlineInput } from '../form-inputs/InlineInput';

import './EditableText.less';

const block = bemBlock('editable-text');

type EditableTextProps = {
  value: string;
  placeholder?: string;
  isEditing: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  className?: string;
  inputClassName?: string;
  'data-role'?: string;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onSubmit: (value: string) => void;
};

export const EditableText: React.FC<EditableTextProps> = ({
  value,
  placeholder,
  isEditing,
  isEditable,
  isSelectable,
  className,
  inputClassName,
  'data-role': dataRole,
  onStartEditing,
  onStopEditing,
  onSubmit,
}) => {
  const innerInputRef = React.useRef<HTMLInputElement | null>(null);
  const textValue = value || placeholder;
  const inputPlaceholder = placeholder ?? value;

  const handleRef = React.useCallback((ref: HTMLInputElement | null) => {
    innerInputRef.current = ref;
  }, []);

  const handleStartEditing = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onStartEditing();
  };

  React.useEffect(() => {
    if (isEditing) {
      innerInputRef.current?.focus();
    }
  }, [isEditing]);

  return (
    <Layout.Row
      className={block({
        modifiers: {
          editable: isEditable && !isEditing,
          selectable: isSelectable && !isEditing,
        },
        extra: className,
      })}
      alignItems="center"
      data-role={dataRole}
      span="shrink"
    >
      {isEditing ? (
        <InlineInput
          className={block({ element: 'input', extra: inputClassName })}
          inputRef={handleRef}
          value={value}
          placeholder={inputPlaceholder}
          data-role="editable-text-input"
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
          onSubmit={onSubmit}
          onCancel={onStopEditing}
        />
      ) : (
        <>
          <Text
            className={block('text')}
            fontWeight="bold"
            lineHeight="xxl"
            size="md"
            ellipsis
            onMouseDown={stopPropagation}
            onClick={stopPropagation}
            data-widget-title={textValue}
            data-role="editable-text-text"
            onDoubleClick={isEditable ? onStartEditing : undefined}
            title={textValue}
          >
            {textValue}
          </Text>
          {isEditable && (
            <Button
              className={block('edit-button')}
              onClick={handleStartEditing}
              onMouseDown={stopPropagation}
              data-role="editable-text-edit"
              variant="tertiary"
              size="sm"
              square
            >
              <Icon size="lg" fixedWidth color="text-subdued" glyph="pencil" />
            </Button>
          )}
        </>
      )}
    </Layout.Row>
  );
};
